/**
 * Brookfarm add to cart.
 *
 * One delegated handler for every add-to-cart form in the theme — the PDP and the shared
 * product card (homepage slider, collection grid, and whatever renders it next). Forms are
 * matched on their action rather than an opt-in attribute: Liquid's `form` tag does not take
 * hyphenated attribute keys cleanly, and every cart form posts to the same route anyway.
 *
 * Why not Dawn's <product-form>: Rebuy's Smart Cart is the D2C cart on this store. It patches
 * fetch and opens its own flyout whenever something is added, so Dawn's element opened a
 * second drawer on top of it. Removing Dawn's drawer is not a fix either — with no cart
 * element present, product-form falls back to `window.location = routes.cart_url`.
 *
 * So this posts to /cart/add.js and stops there. Rebuy reacts to the fetch; nothing here
 * renders a cart. The only thing published is Dawn's `cart-update`, which the header badge
 * listens on.
 *
 * Delegated at the document rather than bound per form, so cards rendered later — by the
 * Section Rendering API, or a future quick-add — are covered without re-binding.
 */
(() => {
  if (window.__brookfarmCartBound) return;
  window.__brookfarmCartBound = true;

  const setBusy = (form, busy) => {
    const button = form.querySelector('[type="submit"]');
    if (button) {
      button.toggleAttribute('aria-disabled', busy);
      button.classList.toggle('loading', busy);
    }

    /* Swap the label for "Adding..." while the post is in flight. There is no spinner: the
       label change is the whole busy affordance, so a spinner alongside it just sat on top
       of the text. Nothing this handler serves renders one any more. The string is carried on
       the button by Liquid rather than written here: this file is a static asset with no
       access to `t`, and hardcoding English would defeat the point of the whole rebuild.

       The idle label is stashed on first use instead of being read from a second data
       attribute, so it survives whatever the button said - "Add to cart", "Sold out", or a
       label the PDP's variant re-render just replaced it with. */
    const label = button && button.querySelector('[data-bf-atc-label]');
    if (!label || !button.dataset.adding) return;

    if (busy) {
      if (label.dataset.idleLabel === undefined) label.dataset.idleLabel = label.textContent.trim();
      label.textContent = button.dataset.adding;
    } else if (label.dataset.idleLabel !== undefined) {
      label.textContent = label.dataset.idleLabel;
    }
  };

  const showError = (form, message) => {
    const box = form.querySelector('[data-bf-form-error]') || form.parentElement?.querySelector('[data-bf-form-error]');
    if (!box) return;
    box.textContent = message || '';
    box.hidden = !message;
  };

  /* Capture phase, deliberately. Another script on the page (Rebuy's bundle) stops the
     submit event during bubbling, so a bubble-phase listener on document never ran and the
     form posted natively to /cart. Capturing gets us in before anything can stop it. */
  document.addEventListener(
    'submit',
    async (event) => {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form) return;

    event.preventDefault();

    const button = form.querySelector('[type="submit"]');
    if (button?.hasAttribute('aria-disabled')) return;

    setBusy(form, true);
    showError(form, '');

    try {
      const response = await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body: new FormData(form),
      });
      const body = await response.json();

      if (!response.ok || body.status) {
        showError(form, body.description || body.message);
        return;
      }

      if (window.publish) window.publish('cart-update', { source: 'brookfarm-add-to-cart' });
    } catch (error) {
      console.error('[Brookfarm] add to cart failed', error);
      showError(form, '');
    } finally {
      setBusy(form, false);
    }
    },
    true
  );
})();
